import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
	isNativeComponentsEnabled,
	isNativeBottomNavigationEnabled,
	isNativeNavHeaderEnabled
} from '~/components/app-bridge/app-properties.js';
import { isSmallerSelector } from '~/components/layout/mediaquery.js';
import {
	selectIsDesktop,
	selectCasinoMaintenanceEnabled,
	selectGlobalNavigationEnabled
} from '~/components/brand/ducks/selectors';

import {
	locationHasHeader,
	locationHasSmallerBrandingHeader,
	locationHasSubHeader,
	locationHasProductNav,
	locationHasHeroHeader,
	locationHasSmallSubHeader,
	locationHasSearchBar,
	locationForceAbsoluteHeader,
	locationForceAbsoluteSubHeader,
	locationHasTopNav
} from './routes/utils.js';
import useLocationHasFooter from './use-location-has-footer.js';
import InitialUIContextProvider from './initial-ui-context-provider.js';
import createContexts from './create-contexts.js';
import { OVERLAY, SIDEBAR, MODAL } from '~/ui/ui-constants.ts';

import SIDEBAR_PATHS from './sidebar-paths.js';
import OVERLAY_PATHS from './overlay-paths.js';
import MODAL_PATHS from './modal-paths.js';

const contexts = createContexts([
	[SIDEBAR, SIDEBAR_PATHS],
	[OVERLAY, OVERLAY_PATHS],
	[MODAL, MODAL_PATHS]
]);

const InitialUIContext = ({ children }) => {
	const location = useLocation();
	const smaller = useSelector(isSmallerSelector);
	const isDesktop = useSelector(selectIsDesktop);
	const isCasinoMaintenanceEnabled = useSelector(selectCasinoMaintenanceEnabled);
	const globalNavigationEnabled = useSelector(selectGlobalNavigationEnabled);

	const hasHeader = locationHasHeader(location, isDesktop);
	const hasSmallerBrandingHeader = locationHasSmallerBrandingHeader(location, globalNavigationEnabled);
	const hasNativeHeader = isNativeNavHeaderEnabled();
	const hasNativeBottomNavigation = isNativeBottomNavigationEnabled();
	const hasTopNavigation = globalNavigationEnabled && smaller && locationHasTopNav(location) && !hasNativeHeader;
	const forceAbsoluteHeader = locationForceAbsoluteHeader(location);
	const hasSubHeader = locationHasSubHeader(location, isDesktop);
	const forceAbsoluteSubHeader = locationForceAbsoluteSubHeader(location);
	const hasSmallSubHeader = locationHasSmallSubHeader(location);
	const hasSearchBar = locationHasSearchBar(location, !smaller, isCasinoMaintenanceEnabled, globalNavigationEnabled);
	const hasProductNav = locationHasProductNav(location, isDesktop);
	const hasHeroHeader = locationHasHeroHeader(location);
	const hasFooter = useLocationHasFooter();

	const nativeComponentEnabled = isNativeComponentsEnabled();
	const hasOverlay = smaller || nativeComponentEnabled;
	const hasSidebar = true;
	const hasModal = true;

	return (
		<InitialUIContextProvider
			contexts={contexts}
			hasHeader={hasHeader}
			hasSmallerBrandingHeader={hasSmallerBrandingHeader}
			hasTopNavigation={hasTopNavigation}
			hasSubHeader={hasSubHeader}
			hasSmallSubHeader={hasSmallSubHeader}
			hasSearchBar={hasSearchBar}
			hasProductNav={hasProductNav}
			hasHeroHeader={hasHeroHeader}
			hasFooter={hasFooter}
			hasOverlay={hasOverlay}
			hasSidebar={hasSidebar}
			hasModal={hasModal}
			forceAbsoluteHeader={forceAbsoluteHeader}
			forceAbsoluteSubHeader={forceAbsoluteSubHeader}
			hasNativeHeader={hasNativeHeader}
			hasNativeBottomNavigation={hasNativeBottomNavigation}
		>
			{children}
		</InitialUIContextProvider>
	);
};

export default InitialUIContext;
